import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useOpsCenterStore } from '../../store'
import type { Workflow, WebhookConfig } from '../../types'
import { Field, TextInput } from './fields'

export interface WebhookDrawerProps {
  wf: Workflow
}

export function WebhookDrawer({ wf }: WebhookDrawerProps) {
  const updateWorkflow = useOpsCenterStore((s) => s.updateWorkflow)
  const webhook = wf.webhook
  const headers = webhook.headers ?? {}

  const [newHeaderKey, setNewHeaderKey] = useState('')
  const [newHeaderValue, setNewHeaderValue] = useState('')

  function patch(p: Partial<WebhookConfig>) {
    updateWorkflow(wf.id, { webhook: { ...webhook, ...p } })
  }

  function updateHeaderValue(key: string, value: string) {
    patch({ headers: { ...headers, [key]: value } })
  }

  function removeHeader(key: string) {
    const next = { ...headers }
    delete next[key]
    patch({ headers: next })
  }

  function addHeader() {
    const key = newHeaderKey.trim()
    if (!key) return
    patch({ headers: { ...headers, [key]: newHeaderValue } })
    setNewHeaderKey('')
    setNewHeaderValue('')
  }

  return (
    <div className="flex flex-col gap-4">
      <Field label="Client URL">
        <TextInput
          value={webhook.url}
          onChange={(e) => patch({ url: e.target.value })}
          placeholder="https://client.example.com/webhook"
        />
      </Field>

      <Field label="Retry count">
        <TextInput
          type="number"
          min={0}
          value={webhook.retryCount ?? 0}
          onChange={(e) => patch({ retryCount: Number(e.target.value) })}
        />
      </Field>

      <Field label="Events (comma-separated)">
        <TextInput
          value={(webhook.events ?? []).join(', ')}
          onChange={(e) =>
            patch({
              events: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="workflow.completed, workflow.failed"
        />
      </Field>

      <Field label="Headers">
        <div className="flex flex-col gap-1.5">
          {Object.entries(headers).map(([key, value]) => (
            <div key={key} className="flex items-center gap-1.5">
              <TextInput value={key} readOnly className="flex-1 opacity-70" />
              <TextInput value={value} onChange={(e) => updateHeaderValue(key, e.target.value)} className="flex-1" />
              <button
                onClick={() => removeHeader(key)}
                className="p-1 rounded text-[#5b6b85] hover:text-red-400 hover:bg-[#1a2740] shrink-0"
                title="Remove header"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}

          <div className="flex items-center gap-1.5">
            <TextInput
              value={newHeaderKey}
              onChange={(e) => setNewHeaderKey(e.target.value)}
              placeholder="Header name"
              className="flex-1"
            />
            <TextInput
              value={newHeaderValue}
              onChange={(e) => setNewHeaderValue(e.target.value)}
              placeholder="Value"
              className="flex-1"
            />
            <button
              onClick={addHeader}
              className="p-1 rounded text-[#94a3b8] hover:text-[var(--text-primary)] hover:bg-[#1a2740] shrink-0"
              title="Add header"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      </Field>
    </div>
  )
}
